/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shopping mode — the screen used one-handed, in a shop, possibly offline
 * (docs/SHOPPING_LIST_10X.md §4.4).
 *
 *   • "Where are you shopping?" — the store sets the walking order of the
 *     categories, narrows the list to that store's things (plus the ones
 *     with no store), and tells the family "Nathan is shopping at Tesco".
 *   • big targets; ticked items drop into a collapsed tray
 *   • together: everyone's ticks appear live; claim a category ("I'll do
 *     produce") so two people don't both fetch the milk
 *   • "Couldn't find it" keeps an item for next time instead of losing it
 *   • Finish: what you couldn't find goes back on Home, food goes into the
 *     pantry, ticked items clear
 *   • the screen stays awake, and it works with the radio off — and says so
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ShoppingApi } from "../../api/endpoints.ts";
import type { ItemSource } from "../../api/types.ts";
import type { LiveItem } from "../../api/shoppingLive.ts";
import { Banner, ErrorBox, Loading, Modal } from "../../components/ui.tsx";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { LiveBadge, useShoppingLive, useUndoable } from "./live/useShoppingLive.tsx";
import { shoppingEngine } from "./live/engine.ts";
import {
  claimOf, describeQuantity, grouped, homeListId, itemsOf, memberName, sessionsOn, storeName,
  walkingOrder, whoAdded, type Model,
} from "./live/model.ts";

const storeKey = (listId: string) => `mp.shopping.store.${listId}`;
function rememberedStore(listId: string): string | null | undefined {
  try {
    const v = localStorage.getItem(storeKey(listId));
    return v === null ? undefined : v === "" ? null : v;
  } catch { return undefined; }
}

/** Keep the screen on while shopping, where the browser allows it. */
function useWakeLock(on: boolean) {
  useEffect(() => {
    if (!on) return;
    type Sentinel = { release: () => Promise<void> };
    const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<Sentinel> } };
    let lock: Sentinel | null = null;
    let cancelled = false;
    const acquire = () => {
      nav.wakeLock?.request("screen").then((l) => { if (cancelled) void l.release(); else lock = l; }).catch(() => {});
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release().catch(() => {});
    };
  }, [on]);
}

export function ShoppingModeView() {
  const { id: rawId = "" } = useParams();
  const navigate = useNavigate();
  const { model, status, error, unsent } = useShoppingLive();
  const { run, bar } = useUndoable(model);
  const [store, setStore] = useState<string | null | undefined>(() => rememberedStore(rawId));
  const [choosing, setChoosing] = useState(false);
  const [onlyHere, setOnlyHere] = useState(true);
  const [showGot, setShowGot] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [sourcesFor, setSourcesFor] = useState<LiveItem | null>(null);
  const [adding, setAdding] = useState("");
  useWakeLock(true);

  const id = model && rawId === "home" ? homeListId(model) ?? "" : rawId;
  const list = model?.lists[id];
  const mine = model ? sessionsOn(model, id).find((s) => s.member_sub === model.me) : undefined;

  // Starting to shop (once per visit) is what tells the family.
  useEffect(() => {
    if (!model || !list || store === undefined || mine) return;
    run([{ op: "list.startShopping", listId: id, storeId: store, sessionId: crypto.randomUUID() }], null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model === null, list?.id, store]);

  const order = useMemo(() => (model ? walkingOrder(model, store ?? null) : []), [model, store]);

  // Leaving shop mode — back to the list, another tab, closing it — means you
  // aren't shopping any more: the family's "is shopping" banner goes, and the
  // sections you claimed are free again. Finishing does this itself.
  const leaving = useRef({ id: "", active: false });
  leaving.current = { id, active: !!mine };
  useEffect(() => () => {
    if (leaving.current.active && leaving.current.id) {
      shoppingEngine.submit([{ op: "list.stopShopping", listId: leaving.current.id }]);
    }
  }, []);

  if (!model) {
    if (status === "error") return <ErrorBox message={error ?? "Couldn't load the list."} />;
    return <Loading label="Loading your list…" />;
  }
  if (!list) {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-4">
        <Link to="/shopping" className="text-sm text-ink-3 hover:text-ink">← Lists</Link>
        <Banner tone="warn">This list isn't here any more.</Banner>
      </div>
    );
  }

  const chooseStore = (s: string | null) => {
    setStore(s);
    setChoosing(false);
    try { localStorage.setItem(storeKey(id), s ?? ""); } catch { /* private mode */ }
    if (mine) run([{ op: "list.startShopping", listId: id, storeId: s, sessionId: crypto.randomUUID() }], null);
  };

  if (store === undefined || choosing) {
    return <StoreChooser model={model} title={list.title} current={store}
      onChoose={chooseStore} onBack={() => (store === undefined ? navigate(`/shopping/${id}`) : setChoosing(false))} />;
  }

  const all = itemsOf(model, id);
  const groups = grouped(model, id, { store: onlyHere && store ? store : undefined, order })
    .map((g) => ({ ...g, items: g.items.filter((i) => i.status === "needed") }))
    .filter((g) => g.items.length);
  const missing = all.filter((i) => i.status === "unavailable");
  const got = all.filter((i) => i.status === "got");
  const elsewhere = store && onlyHere
    ? all.filter((i) => i.status === "needed" && i.store_id && i.store_id !== store).length : 0;
  const total = all.length;
  const others = sessionsOn(model, id).filter((s) => s.member_sub !== model.me);

  const tick = (i: LiveItem) => run([{ op: "item.status", itemId: i.id, status: i.status === "got" ? "needed" : "got" }], null);

  return (
    <div className="mx-auto max-w-xl space-y-3 p-3 pb-28">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/shopping/${id}`} className="text-sm text-ink-3 hover:text-ink">← List</Link>
        <div className="min-w-0 text-center">
          <h1 className="type-section truncate">{list.title}</h1>
          <button className="text-xs text-accent hover:underline" onClick={() => setChoosing(true)}>
            {store ? `at ${storeName(model, store) ?? "a store"}` : "any store"} · change
          </button>
        </div>
        <span className="text-sm text-ink-3">{got.length}/{total}</span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-paper-3" role="progressbar"
        aria-valuenow={got.length} aria-valuemin={0} aria-valuemax={total}>
        <div className="h-full bg-accent transition-all" style={{ width: total ? `${(got.length / total) * 100}%` : "0%" }} />
      </div>
      <div className="flex items-center justify-between">
        <LiveBadge status={status} unsent={unsent} />
        {store && (
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            <input type="checkbox" checked={onlyHere} onChange={(e) => setOnlyHere(e.target.checked)} />
            Only {storeName(model, store)}'s things{elsewhere ? ` (${elsewhere} for other stores hidden)` : ""}
          </label>
        )}
      </div>

      {status === "offline" && (
        <Banner tone="warn">You're offline. Ticking still works — it syncs when you're back.</Banner>
      )}
      {others.length > 0 && (
        <Banner>
          {others.map((s) => `${memberName(model, s.member_sub)}${s.store_id ? ` (at ${storeName(model, s.store_id)})` : ""}`).join(", ")}
          {others.length === 1 ? " is" : " are"} shopping this list too — claim a section so you don't both fetch it.
        </Banner>
      )}
      {list.is_stale && (
        <Banner tone="warn">
          The meal plan changed after it was added.{" "}
          <Link className="underline" to={`/shopping/${id}`}>Review the differences</Link>.
        </Banner>
      )}

      {groups.length === 0 && missing.length === 0 && (
        <p className="py-8 text-center text-sm text-ink-3">
          {total ? "Everything's in the trolley. " : "Nothing on this list. "}
          {total > 0 && <button className="text-accent underline" onClick={() => setFinishing(true)}>Finish the trip</button>}
        </p>
      )}

      {groups.map(({ category, items }) => {
        const claim = claimOf(model, id, category.key);
        const claimedByOther = claim && claim.member_sub !== model.me;
        return (
          <section key={category.key}>
            <div className="flex items-center justify-between px-1 py-1">
              <h2 className="text-xs font-medium uppercase tracking-wide text-ink-3">
                {category.icon ? `${category.icon} ` : ""}{category.name} · {items.length}
              </h2>
              {claim && !claimedByOther ? (
                <button className="chip border-accent/40 text-accent"
                  onClick={() => run([{ op: "claim.clear", listId: id, category: category.key }], null)}>
                  You're on it · release
                </button>
              ) : claimedByOther ? (
                <button className="chip" title="Take this section over"
                  onClick={() => run([{ op: "claim.set", listId: id, category: category.key, force: true }], null)}>
                  {memberName(model, claim!.member_sub)}'s doing this
                </button>
              ) : (
                <button className="chip-toggle"
                  onClick={() => run([{ op: "claim.set", listId: id, category: category.key }], null)}>
                  I'll do this
                </button>
              )}
            </div>
            <div className={`space-y-1 ${claimedByOther ? "opacity-60" : ""}`}>
              {items.map((i) => (
                <BigRow key={i.id} item={i} model={model}
                  onTick={() => tick(i)}
                  onMissing={() => run([{ op: "item.status", itemId: i.id, status: "unavailable" }], `Couldn't find ${i.display_name}`)}
                  onSources={() => setSourcesFor(i)} />
              ))}
            </div>
          </section>
        );
      })}

      {missing.length > 0 && (
        <section>
          <h2 className="px-1 py-1 text-xs font-medium uppercase tracking-wide text-warn">Couldn't find · {missing.length}</h2>
          <div className="space-y-1">
            {missing.map((i) => (
              <BigRow key={i.id} item={i} model={model}
                onTick={() => tick(i)}
                onMissing={() => run([{ op: "item.status", itemId: i.id, status: "needed" }], null)}
                onSources={() => setSourcesFor(i)} />
            ))}
          </div>
        </section>
      )}

      {got.length > 0 && (
        <button className="btn-ghost w-full" onClick={() => setShowGot((s) => !s)}>
          {showGot ? "Hide" : "Show"} {got.length} in the trolley
        </button>
      )}
      {showGot && (
        <div className="space-y-1">
          {got.map((i) => (
            <BigRow key={i.id} item={i} model={model} onTick={() => tick(i)} onSources={() => setSourcesFor(i)} />
          ))}
        </div>
      )}

      {/* Fixed so adding something you spotted, or finishing, needs no scroll. */}
      <div className="fixed inset-x-0 bottom-0 border-t border-rule-soft bg-paper-2 p-3">
        <form className="mx-auto flex max-w-xl gap-2" onSubmit={(e) => {
          e.preventDefault();
          const t = adding.trim();
          if (!t) return;
          run([{ op: "item.add", itemId: crypto.randomUUID(), listId: id, text: t,
            ...(store ? { storeId: store } : {}) }], `Added ${t}`);
          setAdding("");
        }}>
          <input className="input" placeholder="Add something…" value={adding}
            onChange={(e) => setAdding(e.target.value)} />
          <button className="btn-ghost" disabled={!adding.trim()}>Add</button>
          <button type="button" className="btn-primary" onClick={() => setFinishing(true)}>Finish</button>
        </form>
      </div>

      {finishing && (
        <FinishModal model={model} listId={id} missing={missing.length} got={got.length}
          onClose={() => setFinishing(false)}
          onFinish={(o) => {
            leaving.current.active = false;
            run([{ op: "list.finishTrip", listId: id, ...o }], "Trip finished");
            try { localStorage.removeItem(storeKey(id)); } catch { /* fine */ }
            navigate(`/shopping/${id}`);
          }} />
      )}
      {sourcesFor && <SourcesModal item={sourcesFor} model={model} onClose={() => setSourcesFor(null)} />}
      {bar}
    </div>
  );
}

function StoreChooser({ model, title, current, onChoose, onBack }: {
  model: Model; title: string; current: string | null | undefined;
  onChoose: (s: string | null) => void; onBack: () => void;
}) {
  return (
    <div className="mx-auto max-w-xl space-y-3 p-4">
      <button className="text-sm text-ink-3 hover:text-ink" onClick={onBack}>← Back</button>
      <h1 className="type-title">Where are you shopping?</h1>
      <p className="text-sm text-ink-3">
        "{title}" follows that store's aisle order and shows its things first — and the family
        sees you're out, so they can add anything they need.
      </p>
      <div className="grid gap-2">
        {model.stores.map((s) => (
          <button key={s.id} className={`card flex items-center gap-3 p-4 text-left ${current === s.id ? "border-accent" : ""}`}
            onClick={() => onChoose(s.id)}>
            <span className="text-2xl" aria-hidden>{s.icon ?? "🏪"}</span>
            <span className="font-medium">{s.name}</span>
          </button>
        ))}
        <button className="card p-4 text-left text-ink-2" onClick={() => onChoose(null)}>
          Anywhere — just the list
        </button>
      </div>
    </div>
  );
}

function BigRow({ item, model, onTick, onMissing, onSources }: {
  item: LiveItem; model: Model; onTick: () => void; onMissing?: () => void; onSources: () => void;
}) {
  const got = item.status === "got";
  const qty = describeQuantity(item);
  const store = storeName(model, item.store_id);
  return (
    <div className={`flex items-center gap-3 rounded-xl2 border px-3 py-3 ${
      got ? "border-rule-soft bg-paper-3/50 text-ink-3" : "border-rule-soft"}`}>
      {/* Deliberately large: this is tapped one-handed, holding a trolley. */}
      <button
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-2 text-lg ${
          got ? "border-accent bg-accent text-paper" : "border-rule"}`}
        onClick={onTick}
        aria-label={got ? `Untick ${item.display_name}` : `Tick ${item.display_name}`}
      >
        {got && <Icon name="check" className="h-5 w-5" />}
      </button>
      <button className="min-w-0 flex-1 text-left" onClick={onSources}>
        <div className={got ? "line-through" : ""}>
          {item.display_name}{qty && <strong className="ml-1.5">{qty}</strong>}
          {item.urgent && !got && <span className="chip ml-2 border-danger/40 text-danger">today</span>}
        </div>
        <div className="truncate text-xs text-ink-3">
          {[store && `at ${store}`, item.brand, item.notes, item.quantity_note, whoAdded(model, item)]
            .filter(Boolean).join(" · ")}
        </div>
      </button>
      {onMissing && !got && (
        <IconButton name={item.status === "unavailable" ? "circle" : "alert"}
          label={item.status === "unavailable" ? `Found ${item.display_name} after all` : `Couldn't find ${item.display_name}`}
          onClick={onMissing} />
      )}
    </div>
  );
}

function FinishModal({ model, listId, missing, got, onClose, onFinish }: {
  model: Model; listId: string; missing: number; got: number; onClose: () => void;
  onFinish: (o: { unavailable: "home" | "keep"; toPantry: boolean; clearGot: boolean }) => void;
}) {
  const isHome = homeListId(model) === listId;
  const [unavailable, setUnavailable] = useState<"home" | "keep">(isHome ? "keep" : "home");
  const [toPantry, setToPantry] = useState(false);
  const [clearGot, setClearGot] = useState(true);
  return (
    <Modal title="Finish the trip" onClose={onClose}>
      <div className="space-y-3 text-sm">
        {missing > 0 && (
          <fieldset className="space-y-1.5">
            <legend className="mb-1 font-medium">{missing} you couldn't find</legend>
            {!isHome && (
              <label className="flex items-center gap-2">
                <input type="radio" checked={unavailable === "home"} onChange={() => setUnavailable("home")} />
                Put back on Home for next time
              </label>
            )}
            <label className="flex items-center gap-2">
              <input type="radio" checked={unavailable === "keep"} onChange={() => setUnavailable("keep")} />
              Keep {isHome ? "them" : "on this list"} as still needed
            </label>
          </fieldset>
        )}
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={toPantry} onChange={(e) => setToPantry(e.target.checked)} />
          Add the food I bought to the pantry
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={clearGot} onChange={(e) => setClearGot(e.target.checked)} />
          Clear the {got} ticked item{got === 1 ? "" : "s"} off the list
        </label>
        <button className="btn-primary w-full" onClick={() => onFinish({ unavailable, toPantry, clearGot })}>
          Finish
        </button>
      </div>
    </Modal>
  );
}

/** "Why is 600 g of chicken on my list?" — answered from stored provenance. */
function SourcesModal({ item, model, onClose }: { item: LiveItem; model: Model; onClose: () => void }) {
  const sources = useQuery({
    queryKey: ["shopping", "sources", item.id],
    queryFn: () => ShoppingApi.sources(item.id),
    enabled: item.origin === "generated",
  });
  return (
    <Modal title={item.display_name} onClose={onClose}>
      {item.origin !== "generated" ? (
        <p className="text-sm text-ink-3">Added by {whoAdded(model, item)}.{item.notes ? ` Note: ${item.notes}` : ""}</p>
      ) : sources.isLoading ? (
        <Loading />
      ) : sources.data?.sources.length ? (
        <ul className="space-y-2 text-sm">
          {sources.data.sources.map((s: ItemSource, i: number) => (
            <li key={i} className="flex justify-between gap-3">
              <span>{s.recipe_title ?? "A planned meal"}</span>
              <span className="text-ink-3">
                {s.plan_date} · {s.contributed_quantity}{s.contributed_unit ? ` ${s.contributed_unit}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-3">No recorded source for this item.</p>
      )}
    </Modal>
  );
}
